// Thin client for the Pages Functions sitting next to this app.
// All requests are same-origin, so the auth cookie rides along automatically.

export interface Camera {
  id: string;
  name: string;
  location: string;
  hls_url: string;
  status: 'live' | 'offline' | 'paused';
}

export interface VisionEvent {
  id: string;
  ts: number;          // unix ms
  camera_id: string;
  event_type: string;  // 'person_detected', 'after_hours_motion', etc.
  confidence: number;
}

export async function checkSession(): Promise<boolean> {
  const res = await fetch('/api/session', { credentials: 'same-origin' });
  return res.ok;
}

export async function login(password: string): Promise<{ ok: boolean; error?: string }> {
  const res = await fetch('/api/login', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ password }),
  });
  if (res.ok) return { ok: true };
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  return { ok: false, error: body.error || 'login failed' };
}

export async function listCameras(): Promise<Camera[]> {
  const res = await fetch('/api/cameras', { credentials: 'same-origin' });
  if (!res.ok) throw new Error('failed to load cameras');
  return res.json();
}

/**
 * Subscribe to the event SSE stream.
 * Returns an unsubscribe function.
 */
export function subscribeEvents(onEvent: (e: VisionEvent) => void): () => void {
  const es = new EventSource('/api/events', { withCredentials: true });
  es.addEventListener('vision-event', (msg) => {
    try {
      const data = JSON.parse((msg as MessageEvent).data) as VisionEvent;
      onEvent(data);
    } catch (err) {
      console.error('bad event payload', err);
    }
  });
  es.onerror = (err) => {
    console.error('SSE error', err);
  };
  return () => es.close();
}
